import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth, API } from '../context/AuthContext';
import { useEntitlements } from '../context/EntitlementsContext';
import AppHeader from '../components/AppHeader';
import BottomNav from '../components/BottomNav';
import {
    SignOut,
    Download,
    Trash,
    Shield,
    User as UserIcon,
    Users,
    CircleNotch,
    Warning,
    X,
} from '@phosphor-icons/react';

/**
 * Account & privacy page.
 * Surfaces the GDPR-compliance endpoints we built into the backend:
 *  - Sign out everywhere → POST /api/auth/logout-all
 *  - Download my data    → GET  /api/account/export
 *  - Delete my account   → POST /api/account/delete
 */
export default function Account() {
    const { user, logout } = useAuth();
    const ent = useEntitlements();
    const navigate = useNavigate();

    const [signingOutAll, setSigningOutAll] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [deleting, setDeleting] = useState(false);

    const handleSignOutAll = async () => {
        if (signingOutAll) return;
        const ok = window.confirm(
            'Sign out of every device that\'s currently logged into your DefectR account? You\'ll need to sign back in here too.',
        );
        if (!ok) return;
        setSigningOutAll(true);
        try {
            const res = await axios.post(`${API}/auth/logout-all`, {}, { withCredentials: true });
            toast.success(`Signed out of ${res.data?.deleted_sessions ?? 0} session${res.data?.deleted_sessions === 1 ? '' : 's'}.`);
            await logout();   // also clears local state, no-op cookie already gone
            navigate('/');
        } catch (err) {
            console.error('logout-all failed', err);
            toast.error('Could not sign out everywhere. Try again in a moment.');
            setSigningOutAll(false);
        }
    };

    const handleExport = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            const res = await axios.get(`${API}/account/export`, { withCredentials: true });
            const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            const stamp = new Date().toISOString().slice(0, 10);
            a.href = url;
            a.download = `defectr-export-${stamp}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1500);
            toast.success('Your data export has been downloaded.');
        } catch (err) {
            console.error('export failed', err);
            const status = err?.response?.status;
            if (status === 429) {
                toast.error('Too many export requests. Try again in an hour.');
            } else {
                toast.error('Could not generate your export. Try again later.');
            }
        } finally {
            setExporting(false);
        }
    };

    const handleDelete = async () => {
        if (deleting) return;
        if (confirmText.trim().toUpperCase() !== 'DELETE') {
            toast.error('Type DELETE to confirm.');
            return;
        }
        setDeleting(true);
        try {
            await axios.post(
                `${API}/account/delete`,
                { confirm: 'DELETE' },
                { withCredentials: true },
            );
            toast.success('Your account and all associated data have been deleted.');
            await logout();
            navigate('/');
        } catch (err) {
            console.error('delete failed', err);
            const status = err?.response?.status;
            if (status === 429) {
                toast.error('Too many deletion attempts. Try again in an hour.');
            } else {
                toast.error('Could not delete account. Contact support@defectr.co.uk.');
            }
            setDeleting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FAFAFA] pb-24">
            <AppHeader title="Account" back />
            <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="account-page">
                {/* Profile */}
                <section className="bg-white border border-[#18181B] p-5">
                    <div className="flex items-center gap-3 mb-4">
                        <UserIcon size={20} weight="duotone" color="#1FA5CF" />
                        <h1 className="font-display text-xl font-semibold tracking-tight">Profile</h1>
                    </div>
                    <dl className="grid grid-cols-3 gap-x-4 gap-y-3 text-sm">
                        <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#52525B]">Name</dt>
                        <dd className="col-span-2 font-display" data-testid="account-name">
                            {user?.name || '—'}
                        </dd>
                        <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#52525B]">Email</dt>
                        <dd className="col-span-2 break-all" data-testid="account-email">
                            {user?.email || '—'}
                        </dd>
                        <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#52525B]">Tier</dt>
                        <dd className="col-span-2 font-mono uppercase tracking-[0.18em] text-xs" data-testid="account-tier">
                            {ent?.tier || '—'}
                        </dd>
                    </dl>
                </section>

                {/* Team workspace shortcut — only visible to team members /
                    admins (`developer_team` tier). Sits above Privacy so
                    the path to billing/seat management is the first thing
                    business users see when they open Account. */}
                {ent?.tier === 'developer_team' && (
                    <section className="bg-[#FAF5FF] border border-[#7C3AED] p-5" data-testid="account-team-card">
                        <div className="flex items-center gap-3 mb-2">
                            <Users size={20} weight="duotone" color="#7C3AED" />
                            <h2 className="font-display text-xl font-semibold tracking-tight">Team workspace</h2>
                        </div>
                        <p className="text-xs text-[#52525B] mb-3">
                            You're on the Developer / Enterprise tier — seats, branding, CSV import and billing live in your team dashboard.
                        </p>
                        <Link
                            to="/team"
                            data-testid="account-open-team-link"
                            className="inline-flex items-center gap-2 bg-[#7C3AED] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#5B21B6]"
                        >
                            Open team dashboard
                        </Link>
                    </section>
                )}

                {/* Privacy & data */}
                <section className="bg-white border border-[#18181B] p-5" data-testid="privacy-section">
                    <div className="flex items-center gap-3 mb-1">
                        <Shield size={20} weight="duotone" color="#1FA5CF" />
                        <h2 className="font-display text-xl font-semibold tracking-tight">Privacy &amp; data</h2>
                    </div>
                    <p className="text-xs text-[#52525B] mb-5">
                        Your DefectR account is yours. Sign out of every device, download a full
                        copy of your data, or delete everything — at any time, no email required.
                    </p>
                    <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#A1A1AA] -mt-3 mb-5">
                        <Link to="/privacy" className="underline hover:text-[#1787AB]" data-testid="account-privacy-link">
                            Privacy &amp; security
                        </Link>
                        <span className="mx-2 text-[#D4D4D8]">·</span>
                        <Link to="/terms" className="underline hover:text-[#1787AB]" data-testid="account-terms-link">
                            Terms &amp; refunds
                        </Link>
                    </p>

                    <div className="space-y-3">
                        {/* Sign out everywhere */}
                        <ActionRow
                            icon={<SignOut size={18} weight="bold" color="#1FA5CF" />}
                            title="Sign out everywhere"
                            body="Invalidates every active session on every device. Useful if you suspect your Google account was compromised."
                            buttonLabel={signingOutAll ? 'Signing out…' : 'Sign out everywhere'}
                            onClick={handleSignOutAll}
                            disabled={signingOutAll}
                            testId="signout-all-btn"
                            buttonClass="border-[#18181B] text-[#09090B] hover:bg-[#F4F4F5]"
                            loading={signingOutAll}
                        />

                        {/* Download my data */}
                        <ActionRow
                            icon={<Download size={18} weight="bold" color="#1FA5CF" />}
                            title="Download my data"
                            body="A full JSON export of everything we hold — properties, defects, photos (as paths), and payment-receipt records (Stripe session ID, amount, status — never card numbers). GDPR Article 20."
                            buttonLabel={exporting ? 'Building…' : 'Download'}
                            onClick={handleExport}
                            disabled={exporting}
                            testId="export-data-btn"
                            buttonClass="border-[#18181B] text-[#09090B] hover:bg-[#F4F4F5]"
                            loading={exporting}
                        />

                        {/* Delete account */}
                        <ActionRow
                            icon={<Trash size={18} weight="bold" color="#E53935" />}
                            title="Delete my account"
                            body="Permanently erases your account, every property, defect, photo and payment-receipt record. Cannot be undone."
                            buttonLabel="Delete account"
                            onClick={() => setDeleteOpen(true)}
                            disabled={false}
                            testId="open-delete-modal"
                            buttonClass="border-[#E53935] text-[#E53935] hover:bg-[#FDECEA]"
                            danger
                        />
                    </div>
                </section>

                <p className="text-center text-[10px] font-mono uppercase tracking-[0.2em] text-[#A1A1AA]">
                    Questions? <a href="mailto:support@defectr.co.uk" className="underline">support@defectr.co.uk</a>
                </p>
            </main>

            {deleteOpen && (
                <DeleteAccountModal
                    confirmText={confirmText}
                    setConfirmText={setConfirmText}
                    onClose={() => { if (!deleting) { setDeleteOpen(false); setConfirmText(''); } }}
                    onConfirm={handleDelete}
                    deleting={deleting}
                />
            )}

            <BottomNav />
        </div>
    );
}

function ActionRow({ icon, title, body, buttonLabel, onClick, disabled, testId, buttonClass, danger = false, loading = false }) {
    return (
        <div
            className={`border ${danger ? 'border-[#FECACA] bg-[#FFF5F5]' : 'border-[#E4E4E7]'} p-4 flex items-start gap-3`}
        >
            <div className="flex-none mt-0.5">{icon}</div>
            <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-sm">{title}</div>
                <p className="text-xs text-[#52525B] mt-1">{body}</p>
            </div>
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                data-testid={testId}
                className={`flex-none inline-flex items-center gap-1.5 px-3 py-2 border font-mono text-[10px] uppercase tracking-[0.18em] active:scale-95 transition-all duration-150 disabled:opacity-50 disabled:cursor-wait ${buttonClass}`}
            >
                {loading && <CircleNotch size={12} weight="bold" className="animate-spin" />}
                {buttonLabel}
            </button>
        </div>
    );
}

function DeleteAccountModal({ confirmText, setConfirmText, onClose, onConfirm, deleting }) {
    return (
        <div
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            data-testid="delete-modal"
            role="dialog"
            aria-modal="true"
        >
            <div className="bg-white w-full max-w-md border-2 border-[#E53935]">
                <div className="border-b border-[#E53935] p-4 flex items-center gap-2">
                    <Warning size={20} weight="fill" color="#E53935" />
                    <span className="font-display font-semibold flex-1">Delete account permanently</span>
                    <button
                        onClick={onClose}
                        disabled={deleting}
                        data-testid="close-delete-modal"
                        className="p-1 hover:bg-[#F4F4F5] disabled:opacity-50"
                    >
                        <X size={18} />
                    </button>
                </div>
                <div className="p-5 space-y-3">
                    <p className="text-sm">
                        This will <strong>permanently erase</strong> your account, every property,
                        every defect, every photo annotation, and every payment record we hold.
                    </p>
                    <ul className="text-xs text-[#52525B] list-disc list-inside space-y-1 bg-[#F4F4F5] p-3">
                        <li>You will be signed out immediately</li>
                        <li>Builders linked to your properties will lose access</li>
                        <li>Active share links will stop working</li>
                        <li>This action cannot be reversed by us or by you</li>
                    </ul>
                    <p className="text-xs text-[#52525B]">
                        Type <strong className="font-mono text-[#E53935]">DELETE</strong> below to confirm.
                    </p>
                    <input
                        autoFocus
                        type="text"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        data-testid="delete-confirm-input"
                        placeholder="DELETE"
                        className="w-full border border-[#E4E4E7] focus:border-[#E53935] outline-none px-3 py-3 font-mono uppercase tracking-[0.2em]"
                        disabled={deleting}
                    />
                </div>
                <div className="border-t border-[#E4E4E7] p-4 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={deleting}
                        data-testid="cancel-delete-btn"
                        className="px-3 py-2 border border-[#18181B] text-[#09090B] hover:bg-[#F4F4F5] font-mono text-[10px] uppercase tracking-[0.18em] disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={deleting || confirmText.trim().toUpperCase() !== 'DELETE'}
                        data-testid="confirm-delete-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#E53935] text-white font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-[#C62828] active:scale-95 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {deleting && <CircleNotch size={12} weight="bold" className="animate-spin" />}
                        {deleting ? 'Deleting…' : 'Delete account'}
                    </button>
                </div>
            </div>
        </div>
    );
}
